/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { observer } from 'mobx-react';
import * as React from 'react';
import { ConsentBanner } from 'src/appShell/components/ConsentBanner';
import { GlobalHeader } from 'src/appShell/globalHeader/GlobalHeader';
import { MaintenancePage } from 'src/appShell/MaintenancePage';
import { SessionExpiredPage } from 'src/appShell/SessionExpiredPage';
import Banner from 'src/banner/Banner';
import { createCluMigrationHandlerGroup } from 'src/clu/modules/handlers/cluMigrationHandlerGroup';
import { createAccountSettingsHandlerGroup } from 'src/core/handlers/accountSettingsHandlerGroup';
import { createAuthHandlerGroup } from 'src/core/handlers/auth/authHandlerGroup';
import { createAzureContainerHandlerGroup } from 'src/core/handlers/azure/azureContainerHandlerGroup';
import { createAzureHandlerGroup } from 'src/core/handlers/azure/azureHandlerGroup';
import { createAzureStorageHandlerGroup } from 'src/core/handlers/azure/azureStorageHandlerGroup';
import { createAzureExpHandlerGroup } from 'src/core/handlers/azureExpHandlerGroup';
import { createConsentHandlerGroup } from 'src/core/handlers/consentHandlerGroup';
import { genericEventHandler } from 'src/core/handlers/genericEventHandler';
import { createTokensHandlerGroup } from 'src/core/handlers/tokensHandlerGroup';
import { createUIConfigsHandlerGroup } from 'src/core/handlers/uiConfigsHandlerGroup';
import { useAuthRefresher } from 'src/core/hooks/useAuthRefresher';
import { useBrowserInfo } from 'src/core/hooks/useBrowserInfo';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useInitializeGlobalEnvironment } from 'src/core/hooks/useInitializeGlobalEnvironment';
import { useInitializeLogger } from 'src/core/hooks/useInitializeLogger';
import { useInstallHandlerGroup } from 'src/core/hooks/useInstallHandlerGroup';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { usePortalUpdater } from 'src/core/hooks/usePortalUpdater';
import { GlobalState } from 'src/core/types/GlobalState';
import { LuisProvider } from 'src/core/utils/contexts/LuisProvider';
import { getEventBusV2 } from 'src/core/utils/eventBusV2';
import { FeatureToggles } from 'src/core/utils/featureToggles';
import { routeTable } from 'src/core/utils/routeTable';
import { createCustomTextTestModelHandlerGroup } from 'src/customText/modules/handlers/customTextTestModelHandlerGroup';
import { createCustomTextDocumentHandlerGroup } from 'src/customText/modules/handlers/documents/customTextDocumentHandlerGroup';
import { createCustomTextProjectHandlerGroup } from 'src/customText/modules/handlers/projects/customTextProjectHandlerGroup';
import { createCustomTextTrainedModelHandlerGroup } from 'src/customText/modules/handlers/trainedModels/customTextTrainedModelHandlerGroup';
import { createAnalyticsHandlerGroup } from 'src/luis/modules/analytics/analyticsHandlerGroup';
import { createLuisAppHandlerGroup } from 'src/luis/modules/apps/handlers/luisAppHandlerGroup';
import { createLuisAppVersionHandlerGroup } from 'src/luis/modules/apps/handlers/luisAppVersionHandlerGroup';
import { createDomainHandlerGroup } from 'src/luis/modules/domains/domainHandlerGroup';
import { createConversationFeatureHandlerGroup } from 'src/luis/modules/features/handlers/conversationFeatureHandlerGroup';
import { createPhraseListHandlerGroup } from 'src/luis/modules/features/handlers/phraseListHandlerGroup';
import { createIntentHandlerGroup } from 'src/luis/modules/intents/intentHandlerGroup';
import { createPatternHandlerGroup } from 'src/luis/modules/patterns/patternHandlerGroup';
import { createTrainingHandlerGroup } from 'src/luis/modules/training/trainingHandlerGroup';
import { createEntityHandlerGroup } from 'src/luis/projects/entities/handlers/entityHandlerGroup';
import { TeachingBubbleOrchestrator } from 'src/luis/projects/events/components/TeachingBubbleOrchestrator/TeachingBubbleOrchestrator';
import { createToastHandlerGroup } from 'src/luis/projects/events/handlers/toastHandlerGroup';
import { createPublishSettingsHandlerGroup } from 'src/luis/projects/publishPane/handlers/publishHandlerGroup';
import { createBatchTestHandlerGroup } from 'src/luis/projects/testPane/handlers/batchTestHandlerGroup';
import { createSingleTestHandlerGroup } from 'src/luis/projects/testPane/handlers/singleTestHandlerGroup';
import { createLabelingHandlerGroup } from 'src/luis/projects/utterances/handlers/labelingHandlerGroup';
import { createSuggestedUtteranceHandlerGroup } from 'src/luis/projects/utterances/handlers/suggestedUtteranceHandlerGroup';
import { createUtteranceHandlerGroup } from 'src/luis/projects/utterances/handlers/utteranceHandlerGroup';
import { Routes } from 'src/Routes';
import { createTranslationHandlerGroup } from 'src/translation/handlers/translationHandlerGroup';
import { createAzureMigrationHandlerGroup } from 'src/verticals/azureMigrationModal/handlers/azureMigrationHandlerGroup';

export type AppProps = GlobalState;

const InnerApp = observer(() => {
    const { rootStore, dispatcher } = useGlobalState();
    const { localize } = useLocalization();
    const { browserType } = useBrowserInfo();
    const { shouldHideBanner } = rootStore;

    rootStore.setBrowserType(browserType);
    useInitializeLogger();
    useAuthRefresher();
    useInitializeGlobalEnvironment();
    usePortalUpdater();
    // to be enabled when applying flighting on any feature.
    // useAzureExp();

    React.useEffect(() => {
        const eventBus = getEventBusV2();

        dispatcher.dispatch('checkLoginStatus', { toastData: {} });
        genericEventHandler('deleteIntent')({ rootStore, eventBus, localize });
    }, []);

    if (FeatureToggles.MaintenanceMode) {
        return <MaintenancePage />;
    }

    if (window.location.pathname.includes(routeTable.sessionExpiredPage())) {
        return <SessionExpiredPage />;
    }

    return (
        <>
            <ConsentBanner />
            <GlobalHeader />
            {!shouldHideBanner && <Banner />}
            <TeachingBubbleOrchestrator />
            <Routes />
        </>
    );
});

/**
 * Represents the root component that hosts
 * the entire portal.
 */
export const App = (props: AppProps) => {
    const { dispatcher, rootStore, uiStore } = props;

    useInstallHandlerGroup(
        dispatcher,
        createAuthHandlerGroup,
        createToastHandlerGroup,
        createAzureHandlerGroup,
        createIntentHandlerGroup,
        createEntityHandlerGroup,
        createDomainHandlerGroup,
        createTokensHandlerGroup,
        createLuisAppHandlerGroup,
        createConsentHandlerGroup,
        createPatternHandlerGroup,
        createTrainingHandlerGroup,
        createLabelingHandlerGroup,
        createAzureExpHandlerGroup,
        createUtteranceHandlerGroup,
        createAnalyticsHandlerGroup,
        createBatchTestHandlerGroup,
        createUIConfigsHandlerGroup,
        createSingleTestHandlerGroup,
        createPhraseListHandlerGroup,
        createTranslationHandlerGroup,
        createCluMigrationHandlerGroup,
        createAzureStorageHandlerGroup,
        createLuisAppVersionHandlerGroup,
        createAzureMigrationHandlerGroup,
        createAzureContainerHandlerGroup,
        createPublishSettingsHandlerGroup,
        createAccountSettingsHandlerGroup,
        createCustomTextProjectHandlerGroup,
        createSuggestedUtteranceHandlerGroup,
        createCustomTextDocumentHandlerGroup,
        createConversationFeatureHandlerGroup,
        createCustomTextTestModelHandlerGroup,
        createCustomTextTrainedModelHandlerGroup
    );

    return (
        <LuisProvider dispatcher={dispatcher} rootStore={rootStore} uiStore={uiStore}>
            <InnerApp />
        </LuisProvider>
    );
};
